/**
 * Journey C — shared status-pill primitive (the one small component extracted
 * during the cross-cutting design-coherence pass).
 *
 * Renders the canonical green/amber/red/blue/neutral status badge used across
 * the agency portal for connected / pending / risk / error states. All colour
 * comes from the semantic design tokens via `styles/components/statusPill.scss`
 * so the same `tone` reads identically on every page.
 *
 * Usage:
 *   <StatusPill tone="success">Connected</StatusPill>
 *   <StatusPill tone="warning" withDot>Pending</StatusPill>
 *   <StatusPill tone="danger" variant="squared">High</StatusPill>
 */
import '../../styles/components/statusPill.scss';

export type StatusTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral';

interface Props {
  tone: StatusTone;
  children: React.ReactNode;
  /** Render a small leading status dot. */
  withDot?: boolean;
  /** `squared` trims the radius for severity/risk-style badges (audit log). */
  variant?: 'default' | 'squared';
  className?: string;
  title?: string;
}

const StatusPill = ({
  tone,
  children,
  withDot = false,
  variant = 'default',
  className = '',
  title,
}: Props) => {
  return (
    <span
      className={`statusPill statusPill--${tone}${
        variant === 'squared' ? ' statusPill--squared' : ''
      }${className ? ` ${className}` : ''}`}
      title={title}
    >
      {withDot && <span className='statusPill__dot' aria-hidden='true' />}
      {children}
    </span>
  );
};

export default StatusPill;
